import React, { useState } from "react"
import { ProfileForm } from "../../components/forms/profileForm"
import { useAppSelector } from "../../hooks/reduxToolkit"
import { useLoadProfileFromServer } from "../../hooks/useLoadProfileFromServer"
import { RequireAuthProvided } from "../../wrappers/requireAuth"
import { useUpdateProfile } from "../../hooks/useUpdateProfile"
import { isInvitedSpouse } from "../../reduxToolkit/membershipSlice"
import { isFamilyPlanRequiredError, useInviteSpouse } from "../../hooks/useInviteSpouse"
import { useSubscriptionPrompt } from "../../hooks/useSubscriptionPrompt"

export interface OnboardingFillProfileProps extends RequireAuthProvided {
  onProfileFilled?: () => Promise<void> | void
}

export function OnboardingFillProfile({user, onProfileFilled}: OnboardingFillProfileProps) {
  const membershipSlice = useAppSelector((s) => s.membership)
  const profile = membershipSlice.profile

  useLoadProfileFromServer()

  const [updateProfileState, updateProfile] = useUpdateProfile()

  // An invited spouse already was the invitee, so inviting a spouse of their own
  // doesn't apply — mirrors the `setMembership` skip in INVITED_SPOUSE_SKIPPED_STEPS.
  const skipInvite = isInvitedSpouse(membershipSlice)

  const [spouseName, setSpouseName] = useState('')
  const [spouseEmail, setSpouseEmail] = useState('')
  const [inviteState, invite] = useInviteSpouse()

  // Inviting a spouse requires the Family plan for Solo subscribers. The no-op
  // custom trigger leaves users without a subscription un-gated (most
  // onboarding users), so only the Solo-tier family check fires.
  const { checkSubscription, SubscriptionPromptModal } = useSubscriptionPrompt({
    trigger: 'custom',
    shouldPrompt: () => false,
    requiresFamilyPlan: () => true,
    upgradeTitle: 'Family Plan Required',
    upgradeMessage: 'Upgrade to the Family plan to invite your spouse as a co-administrator of your membership.',
  })

  return <div className="row mt-4">
    <div className="col-12 col-xl-8 offset-xl-2">
      <h1>Who are you?</h1>
      <p>
        We use your name and date of birth to tell whose expenses are whose — yours,
        your spouse's, or a child's. You can add your phone number, address and
        signature later from your profile; we'll also ask for them at the point we
        need them.
      </p>
      {updateProfileState.error && <div className="alert alert-danger" role="alert">
        {updateProfileState.error.message}
      </div>}
      <ProfileForm initial={profile}
        hideSignature
        hidePhoneAndAddress
        onSubmit={async (profileData) => {
          // Both spouse fields blank means "skip inviting a spouse"; the
          // `required` attributes below (each keyed off the other field) mean
          // the browser's own validation already rejected a submit with
          // exactly one filled, so reaching here means either both are empty
          // or both are populated with a well-formed email.
          const name = spouseName.trim()
          const email = spouseEmail.trim()
          const invitingSpouse = !skipInvite && !!(name && email)

          // Check the subscription gate before persisting anything, so a
          // rejected submission (Solo plan needs to upgrade first) doesn't
          // silently save the profile out from under the member.
          if (invitingSpouse && !checkSubscription()) { return }

          await updateProfile(profileData)

          if (invitingSpouse) {
            try {
              await invite({ email, name })
            } catch {
              // The error is already captured by useAsyncAction and surfaced via
              // inviteState.error below; stay on this step so the member can retry.
              return
            }
          }

          if (onProfileFilled) { await onProfileFilled() }
        }}>
        {!skipInvite && <>
          <hr />
          <h2>Invite your spouse</h2>
          <p>
            Want your spouse to help manage this membership? Invite them as a
            co-administrator and they'll be able to track and submit reimbursements
            alongside you. Leave these blank to skip — you can always do this later.
          </p>

          {inviteState.error && <div className="alert alert-danger" role="alert">
            {isFamilyPlanRequiredError(inviteState.error)
              ? 'The Family plan is required to invite your spouse as a co-administrator. Please upgrade your plan and try again.'
              : "We couldn't send the invitation. Please check the email and try again."}
          </div>}

          <div className="input-group mb-2">
            <span className="input-group-text">Spouse's Name</span>
            <input type="text" className="form-control" id="spouseName"
              required={!!spouseEmail.trim()}
              value={spouseName}
              onChange={(e) => setSpouseName(e.target.value)} />
          </div>

          <div className="input-group mb-3">
            <span className="input-group-text">Spouse's Email</span>
            <input type="email" className="form-control" id="spouseEmail"
              required={!!spouseName.trim()}
              value={spouseEmail}
              onChange={(e) => setSpouseEmail(e.target.value)} />
          </div>
        </>}
      </ProfileForm>
      <SubscriptionPromptModal />
    </div>
  </div>
}
